import { ErrorPanel, Refused } from '@cwos/ui-admin';

/**
 * "We couldn't load X JUST NOW" — and the failures for which that is a lie.
 *
 * Nearly a hundred panels in this app render a failed read as a retryable
 * apology with a "Try again" button under it. That is the right sentence for a
 * dropped connection and for a 500. It is the wrong sentence for a 403, and
 * wrong in a way that costs an operator their afternoon: "just now" promises
 * that waiting will help, and for a permission refusal it never will. The
 * button cannot change the answer. So the operator presses it, reads the same
 * words, presses it again, and concludes the product is broken — instead of
 * asking the one person in their organisation who can grant the grant.
 *
 * This is the SECOND half of a distinction the app already draws once.
 * `listPhase` in `listState.ts` separates `refused` from `failed` and
 * `<ListState>` renders the refusal with no retry, precisely because "asking
 * again cannot change the answer". Roughly two dozen panels go through it; the
 * rest reach for `<ErrorPanel>` directly and lose the distinction on the way.
 * Rather than a `refused` branch written out at every one of those call sites —
 * which is the shape that already failed here twice — the branch lives in this
 * component, and the call sites hand it the error they already had in scope.
 *
 * WHAT THIS IS NOT. It is not the withheld/absent collapse. Every one of these
 * panels renders on `isError`, so none of them was showing an EMPTY state for a
 * refusal — nobody was told their organisation has no exceptions because they
 * could not see the exceptions. The reader always knew something had gone
 * wrong. What they were not told is that it was an answer rather than a fault,
 * and that the remedy is a permission rather than patience.
 */

/**
 * The HTTP status a rejected request carried, or null when it never reached a
 * server that answered.
 *
 * STRUCTURAL, NOT `instanceof`, and that choice is load-bearing. This app
 * throws from TWO unrelated error shapes: `@cwos/api-client`'s
 * `ForbiddenError`/`UnauthorizedError` class hierarchy, and the plain `Error`
 * that `src/lib/api.ts` decorates with `.status` for the sign-in family.
 * Neither is `instanceof` the other. `isForbidden` from the client package
 * gates on the class, so it is blind to the second shape by construction — the
 * same blindness the worker app's `failureKind` was written to escape after
 * every refusal on three of its screens was rendered as "you have no signal".
 * A numeric `status` is the one thing both genuinely share, and an error thrown
 * by a dead connection has none, which is exactly the line being drawn.
 */
export function httpStatusOf(err: unknown): number | null {
  if (typeof err !== 'object' || err === null) return null;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' && Number.isFinite(status) ? status : null;
}

/**
 * The permission the API named in a 403 body, when it named one.
 *
 * `ForbiddenError` lifts it to a field; the transport reads it from
 * `body.permission`. Read from BOTH, and structurally, for the reason above:
 * naming the key is the single most useful thing this screen can say, because
 * it turns "ask somebody" into a request an administrator can act on without a
 * conversation about which screen it was.
 */
export function refusedPermissionOf(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const named = (err as { permission?: unknown }).permission;
  if (typeof named === 'string' && named.trim() !== '') return named.trim();
  const body = (err as { body?: unknown }).body;
  if (typeof body !== 'object' || body === null) return null;
  const fromBody = (body as { permission?: unknown }).permission;
  return typeof fromBody === 'string' && fromBody.trim() !== '' ? fromBody.trim() : null;
}

/**
 * Why a read failed, at the resolution an operator can act on.
 *
 *   'not-allowed' — the server answered 403. This is an ANSWER. Retrying
 *                   repeats it; only a grant changes it.
 *   'signed-out'  — the server answered 401. Retrying repeats it too, but the
 *                   remedy is a sign-in rather than a permission, and telling
 *                   someone to ask their administrator for a grant they
 *                   already hold is its own wasted afternoon.
 *   'dead-link'   — the server answered 400, 404, 410 or 422: the request
 *                   itself names something that is not there or cannot be
 *                   read (a removed record, another organization's id, an id
 *                   that was never a valid id). Retrying sends the same
 *                   request and gets the same answer, so there is no retry —
 *                   only a way back.
 *   'unreachable' — anything else: a 500, a proxy's 502, a dropped connection,
 *                   a request that never got an answer at all. The only kind
 *                   for which "try again" is honest advice.
 *
 * A 404 used to sit in 'unreachable' so screens could keep their own two-world
 * sentence ("It may have been removed, or it may belong to a different
 * organization"). That sentence came with a "Try again" that could never work
 * — the home page's fixture run link produced exactly that loop in production
 * — so the dead-link sentence below carries the same two worlds itself.
 */
export type LoadFailureKind = 'not-allowed' | 'signed-out' | 'dead-link' | 'unreachable';

const DEAD_LINK_STATUSES = new Set([400, 404, 410, 422]);

export function loadFailureKind(err: unknown): LoadFailureKind {
  const status = httpStatusOf(err);
  if (status === 403) return 'not-allowed';
  if (status === 401) return 'signed-out';
  if (status !== null && DEAD_LINK_STATUSES.has(status)) return 'dead-link';
  return 'unreachable';
}

/**
 * The refusal sentence, with the permission in it when the server named one.
 *
 * Exported and tested on its own because a predicate about what an error MEANS
 * has to be assertable without mounting a screen — otherwise the next person to
 * narrow it does so invisibly.
 */
export function refusalMessage(err: unknown): string {
  const permission = refusedPermissionOf(err);
  const opening =
    'Your account does not have access to this. It is not empty and nothing has been left out by mistake — this is an answer, and trying again will repeat it.';
  return permission === null
    ? `${opening} Ask whoever administers your organization for access if you were expecting to see it.`
    : `${opening} It needs the ${permission} permission, which whoever administers your organization can grant.`;
}

export interface LoadFailureProps {
  /** The rejected query's error. `undefined`/`null` render as 'unreachable'. */
  error: unknown;
  /**
   * The screen's own sentence for a retryable failure — usually the one that
   * also says what an empty panel would have wrongly implied ("Do not read this
   * as an empty window"). Only shown for 'unreachable', because it is only true
   * there.
   */
  message?: string;
  /**
   * Wired only into the retryable branch. A refusal gets no retry, ever; for a
   * dead link (with `mainRead`) its presence is what swaps the button for a
   * way back.
   */
  onRetry?: () => void;
  /**
   * THIS IS THE READ THE ROUTE'S OWN URL NAMES — the record a detail screen
   * opened on. Only there is a 400/404/410/422 a dead LINK, so only there does
   * the "This link is no longer valid" card with its Go back appear. Every
   * other read — a widget, a panel embedded in a screen, a side query — keeps
   * its own sentence with no button: a widget's 422 inside the dashboard is
   * not a link the reader followed, and "Go back" would leave a screen that is
   * otherwise fine. Opt-in, so a new caller cannot get the card by accident.
   */
  mainRead?: boolean;
  /** Override the refusal heading where a screen can be more specific. */
  refusedHeading?: string;
}

/** Back where the reader came from; home when the dead link opened a fresh tab. */
function goBack() {
  if (window.history.length > 1) window.history.back();
  else window.location.assign('/');
}

export function LoadFailure({
  error,
  message,
  onRetry,
  refusedHeading,
  mainRead = false,
}: LoadFailureProps) {
  switch (loadFailureKind(error)) {
    case 'not-allowed':
      // No `onRetry`, and this is the entire point of the component: a button
      // that implies asking again might work is worse than no button.
      return <Refused heading={refusedHeading} message={refusalMessage(error)} />;
    case 'signed-out':
      // Also no retry. The next request carries the same dead cookie. This is
      // deliberately NOT a redirect: the app boots to sign-in from `auth.tsx`
      // when /auth/me itself fails, and a panel that yanked the operator out of
      // a half-finished screen on one unlucky 401 would lose their work.
      return (
        <ErrorPanel message="Your sign-in has ended, so this could not be loaded. Sign in again — trying again here will not bring it back, and nothing about your access has changed." />
      );
    case 'dead-link':
      // The card REPLACES a retry that cannot work. A caller that offered no
      // retry is mostly a write reporting a rejected submit (a 400 on "Add
      // escalation target"), where "this link is no longer valid" would be
      // false — so it keeps its own sentence, still with no button. So does any
      // read that is not the route's own (`mainRead`): no retry, no way back.
      if (!mainRead) return <ErrorPanel message={message} />;
      if (onRetry === undefined) return <ErrorPanel message={message} />;
      return (
        <section className="rn-card" role="status">
          <h2 className="rn-card-title">This link is no longer valid</h2>
          <p className="rn-muted">
            What it points to may have been removed, may belong to a different organization or sit
            outside what your account reaches, or the address itself is incomplete. Trying again
            would get the same answer.
          </p>
          <button type="button" className="rn-btn-quiet" onClick={goBack}>
            ← Go back
          </button>
        </section>
      );
    case 'unreachable':
      return <ErrorPanel message={message} onRetry={onRetry} />;
  }
}
